import { useEffect, useRef, useState } from "react";
import { T } from "../styles/GlobalStyles";
import { COLOR_ACCENT_DEFAULT } from "../constants/colors";
import AthleosLogoSvg from "./common/AthleosLogoSvg";

const PHRASE = "Le coaching pro, ça ressemble à ça.";
const TAGLINE = "COACH SMARTER";

const TOTAL_MS = 2200;
const REDUCED_MOTION_MS = 600;

const prefersReducedMotion =
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export default function SplashScreen({ onDone }) {
  const [phase, setPhase] = useState("idle");
  const containerRef = useRef(null);

  useEffect(() => {
    if (prefersReducedMotion) {
      setPhase("static");
      const t = setTimeout(() => {
        setPhase("out");
        setTimeout(onDone, 300);
      }, REDUCED_MOTION_MS);
      return () => clearTimeout(t);
    }

    requestAnimationFrame(() => setPhase("animate"));
    const t = setTimeout(() => {
      setPhase("out");
      setTimeout(onDone, 300);
    }, TOTAL_MS);
    return () => clearTimeout(t);
  }, [onDone]);

  const accent = T.accent || COLOR_ACCENT_DEFAULT;
  const logoSize = 80;
  const iconSize = Math.round(logoSize * 0.72);

  return (
    <div
      ref={containerRef}
      aria-label="Écran de chargement AthleOS"
      role="status"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 99999,
        background: "#0A0A0A",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 18,
        opacity: phase === "out" ? 0 : 1,
        transition: phase === "out" ? "opacity 300ms ease-in" : undefined,
      }}
    >
      {/* Logo container */}
      <div
        style={{
          width: logoSize,
          height: logoSize,
          borderRadius: "22%",
          background: "#0A0A0F",
          border: `1px solid ${accent}40`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: phase === "idle" ? 0 : 1,
          transition: phase !== "idle" ? "opacity 400ms ease-out, filter 300ms ease" : undefined,
          filter:
            phase === "animate"
              ? `drop-shadow(0 0 20px ${accent}80)`
              : "drop-shadow(0 0 0 transparent)",
          animation:
            phase === "animate" ? "splashHalo 600ms ease-out 400ms 1 forwards" : undefined,
        }}
      >
        <AthleosLogoSvg fill={accent} width={iconSize} height={iconSize} />
      </div>

      {/* Wordmark */}
      <span
        className="condensed"
        style={{
          fontWeight: 800,
          fontSize: "1.5rem",
          letterSpacing: 9,
          color: accent,
          textTransform: "none",
          opacity: prefersReducedMotion && phase !== "idle" ? 1 : 0,
          transition: "opacity 400ms ease-out",
          transitionDelay: prefersReducedMotion ? "0ms" : "300ms",
          ...(phase === "animate" || phase === "out" ? { opacity: 1 } : {}),
        }}
      >
        AthleOS
      </span>

      {/* Loader bar */}
      {!prefersReducedMotion && (
        <div
          style={{
            width: 140,
            height: 3,
            background: "rgba(255,255,255,0.06)",
            borderRadius: 3,
            overflow: "hidden",
            opacity: phase === "animate" || phase === "out" ? 1 : 0,
            transition: "opacity 300ms ease-out 500ms",
          }}
        >
          <div
            style={{
              height: "100%",
              borderRadius: 3,
              background: accent,
              boxShadow: `0 0 10px ${accent}80, 0 0 24px ${accent}40`,
              width: phase === "animate" || phase === "out" ? "100%" : "0%",
              transition: "width 900ms ease-in-out 550ms",
            }}
          />
        </div>
      )}

      {/* Buddy phrase */}
      <span
        style={{
          fontSize: "0.72rem",
          letterSpacing: 1.5,
          color: "rgba(255,255,255,0.35)",
          fontStyle: "italic",
          textAlign: "center",
          opacity: prefersReducedMotion ? 0 : undefined,
          ...(phase === "animate" || phase === "out"
            ? { opacity: 1, transition: "opacity 400ms ease-out 700ms" }
            : { opacity: 0, transition: "opacity 400ms ease-out" }),
        }}
      >
        {PHRASE}
      </span>

      {/* Tagline */}
      <span
        style={{
          fontSize: "0.7rem",
          letterSpacing: 4,
          color: "rgba(255,255,255,0.2)",
          textTransform: "uppercase",
          opacity: prefersReducedMotion ? 0 : undefined,
          ...(phase === "animate" || phase === "out"
            ? { opacity: 1, transition: "opacity 400ms ease-out 1000ms" }
            : { opacity: 0, transition: "opacity 400ms ease-out" }),
        }}
      >
        {TAGLINE}
      </span>

      <style>{`
        @keyframes splashHalo {
          0%, 100% { filter: drop-shadow(0 0 0 transparent); }
          50% { filter: drop-shadow(0 0 20px ${accent}80); }
        }
      `}</style>
    </div>
  );
}
